'use client';

import { useState, useTransition } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import {
  type DsaReportInput,
  DsaReportSchema,
} from '../_lib/dsa-report.schema';
import { sendDsaReport } from '../_lib/server/server-actions';

/**
 * DSA-Meldeformular – Prototyp-Markup (.locari-form/.field/.form-hint/
 * .form-checkbox/.form-note/.form-success) verdrahtet mit react-hook-form + Zod
 * gegen die Server-Action (Notice-and-Action, Art. 16 DSA). Der leere
 * content_type-Select-Stub aus dem Prototyp ist bewusst weggelassen.
 */
export function DsaReportForm() {
  const [submitted, setSubmitted] = useState(false);
  const [pending, startTransition] = useTransition();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<DsaReportInput>({
    resolver: zodResolver(DsaReportSchema),
    defaultValues: {
      name: '',
      email: '',
      contentLocation: '',
      reason: '',
      goodFaith: undefined,
    },
  });

  const onSubmit = handleSubmit((values) => {
    startTransition(async () => {
      try {
        await sendDsaReport(values);
        setSubmitted(true);
      } catch {
        setError('root', {
          message:
            'Senden fehlgeschlagen. Bitte versuche es erneut oder schreib uns direkt.',
        });
      }
    });
  });

  if (submitted) {
    return (
      <div className="form-success is-visible" role="status" aria-live="polite">
        <span className="success-mark" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </span>
        <h3>Deine Meldung ist eingegangen.</h3>
        <p>
          Wir behandeln sie vertraulich und prüfen sie gemäß Art. 16 Digital
          Services Act (DSA). Eine Eingangsbestätigung haben wir dir per E-Mail
          geschickt.
        </p>
      </div>
    );
  }

  return (
    <form className="locari-form" onSubmit={onSubmit} noValidate>
      <div className="field">
        <label htmlFor="dsa-name">
          <span>Dein Name</span>
        </label>
        <input
          id="dsa-name"
          type="text"
          maxLength={200}
          autoComplete="name"
          {...register('name')}
        />
        {errors.name ? (
          <p className="field-error">{errors.name.message}</p>
        ) : null}
      </div>

      <div className="field">
        <label htmlFor="dsa-email">
          <span>Deine E-Mail-Adresse</span>
        </label>
        <input
          id="dsa-email"
          type="email"
          autoComplete="email"
          {...register('email')}
        />
        {errors.email ? (
          <p className="field-error">{errors.email.message}</p>
        ) : null}
      </div>

      <div className="field">
        <label htmlFor="dsa-content-location">
          <span>Wo befindet sich der Inhalt?</span>
        </label>
        <textarea
          id="dsa-content-location"
          maxLength={2000}
          placeholder="z.B. URL, Screenshot-Beschreibung..."
          {...register('contentLocation')}
        />
        <p className="form-hint">
          Bitte gib die URL oder eine genaue Beschreibung an, wo der Inhalt zu
          finden ist (z.B. Screenshot, Zeitstempel).
        </p>
        {errors.contentLocation ? (
          <p className="field-error">{errors.contentLocation.message}</p>
        ) : null}
      </div>

      <div className="field">
        <label htmlFor="dsa-reason">
          <span>Begründung</span>
        </label>
        <textarea
          id="dsa-reason"
          minLength={20}
          maxLength={5000}
          {...register('reason')}
        />
        <p className="form-hint">
          Erläutere ausführlich, warum du den Inhalt als rechtswidrig ansiehst.
        </p>
        {errors.reason ? (
          <p className="field-error">{errors.reason.message}</p>
        ) : null}
      </div>

      <div className="form-checkbox">
        <input id="dsa-good-faith" type="checkbox" {...register('goodFaith')} />
        <label htmlFor="dsa-good-faith">
          Ich bestätige, dass ich in gutem Glauben davon überzeugt bin, dass die
          in dieser Meldung enthaltenen Angaben richtig und vollständig sind.
        </label>
      </div>
      {errors.goodFaith ? (
        <p className="field-error">{errors.goodFaith.message}</p>
      ) : null}

      <div className="submit-row">
        <button
          type="submit"
          className="btn btn-primary btn-lg"
          disabled={pending}
        >
          {pending ? 'Senden…' : 'Meldung absenden'}
        </button>
      </div>

      <p className="form-note">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        <span>
          Deine Meldung wird <strong>vertraulich</strong> behandelt und gemäß
          Art. 16 Digital Services Act (DSA) geprüft.
        </span>
      </p>

      {errors.root ? (
        <p className="field-error" role="alert">
          {errors.root.message}
        </p>
      ) : null}
    </form>
  );
}
